/* eslint-disable indent */
"use client";

import { FC, useCallback, useContext, useEffect, useState } from "react";
import ReactPlayer from "react-player";
import { toast } from "react-toastify";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { captureException } from "@sentry/react";
import { Play } from "lucide-react";
import { useRouter } from "next/router";
import SocialShare from "components/atoms/SocialShare";
import Tabs from "components/atoms/Tab";
import { ADetailerModal } from "components/molecules/ADetailerModal";
import { BGRemovalModal } from "components/molecules/BGRemovalModal";
import CanvasModal from "components/molecules/CanvasModal";
import CommentSection from "components/molecules/CommentSection";
import ControlsContainer from "components/molecules/ControlsContainer";
import { DeleteConfirmationModal } from "components/molecules/DeleteModal";
import { EditModal } from "components/molecules/EditModal";
import EligibleCompetitionsModal from "components/molecules/EligibleCompetitionsModal";
import { FaceSwapModal } from "components/molecules/FaceSwapModal";
import { InpaintingModal } from "components/molecules/InpaintingModal";
import MetadataSection from "components/molecules/MetadataSection";
import ShareModal from "components/molecules/ShareModal";
import StatsSection from "components/molecules/StatsSection";
import { UpscalerModal } from "components/molecules/UpscalerModal";
import { deleteImage, getCommentsByImage, getMonsterStatsByImageId, getNPCStatsByImageId, onToggleVisibility, voteOnImage } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, GameEntityDTO, ICommentDTO, ImageDTO, RequestType, UserPremiumType } from "types";
import { calculateTotalCommentsSize } from "utils/utils";
import * as Styled from "./ImagePage.styled";
interface ImagePageProps {
  imageDto: ImageDTO;
}
export const ImagePage: FC<ImagePageProps> = ({
  imageDto
}) => {
  const router = useRouter();
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [originalImage, setOriginalImage] = useState<ImageDTO>(imageDto);
  const [selectedImage, setSelectedImage] = useState<ImageDTO>(imageDto); // This will change based on user selection
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showEligibleCompetitionsModal, setShowEligibleCompetitionsModal] = useState(false);
  const [comments, setComments] = useState<ICommentDTO[]>([]);
  const [stats, setStats] = useState<GameEntityDTO | null>(null);
  const [editImageId, setEditImageId] = useState<string | null>(null); // Track the image being edited
  const [showEditModal, setShowEditModal] = useState(false);
  const [isADetailerModalOpen, setIsADetailerModalOpen] = useState(false);
  const [isFaceSwapModalOpen, setIsFaceSwapModalOpen] = useState(false);
  const [isBGRemovalModalOpen, setIsBGRemovalModalOpen] = useState(false);
  const [isCanvasModalOpen, setCanvasModalOpen] = useState<boolean>(false);
  const [isInpaintingModalOpen, setInpaintingModalOpen] = useState(false);
  const [base64Image, setBase64Image] = useState<string | null>(null);
  const [isUpscalerModalOpen, setUpscalerModalOpen] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({
    width: 1,
    height: 1
  });
  const handleInpaintClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setCanvasModalOpen(true);
    }
  };
  const handleCloseCanvasModal = () => {
    setCanvasModalOpen(false);
  };
  const handleMaskSubmit = (maskData: string) => {
    setBase64Image(maskData); // Store the base64 data from the canvas
    setCanvasModalOpen(false); // Close the canvas modal
    setInpaintingModalOpen(true); // Open the inpainting modal
  };
  const handleCloseInpaintingModal = () => {
    setInpaintingModalOpen(false);
    setBase64Image(null); // Clear the image data
  };
  useEffect(() => {
    setOriginalImage(imageDto);
    setSelectedImage(imageDto);
  }, [imageDto]);
  const handleCloseBGRemovalModal = () => {
    setIsBGRemovalModalOpen(false);
  };
  const handleBGRemovalClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    setIsBGRemovalModalOpen(true);
  };
  const handleCloseUpscalerModal = () => {
    setUpscalerModalOpen(false);
  };
  const handleUpscalerModalClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (selectedImage.imageMetadata.npcFieldsMap["Edit Type"] === "UPSCALER") {
      toast.error("This image has already been upscaled and cannot be upscaled again.");
    } else {
      setUpscalerModalOpen(true);
    }
  };
  const handleADetailerClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setIsADetailerModalOpen(true);
    }
  };
  const handleCloseADetailerModal = () => {
    setIsADetailerModalOpen(false);
  };
  const handleEditCancel = () => {
    setShowEditModal(false);
  };
  const handleCloseFaceSwapModal = () => {
    setIsFaceSwapModalOpen(false);
  };
  const handleFaceSwapClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setIsFaceSwapModalOpen(true);
    }
  };
  const handleEditClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setShowEditModal(true);
    }
  };
  const handleShareClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsShareModalOpen(true);
  };
  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };
  const handleCompetitionsClick = () => {
    setShowEligibleCompetitionsModal(true);
  };
  const handleCloseEligibleCompetitionsModal = () => {
    setShowEligibleCompetitionsModal(false);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  const prepareForDelete = () => {
    setShowDeleteConfirmation(true);
  };
  const handleConfirmDelete = async () => {
    try {
      await deleteImage(selectedImage.id);
      if (selectedImage.id === originalImage.id) {
        // If the original image is being deleted, route to the home page
        router.push("/");
      } else {
        // If a child image is being deleted, update the original image's child images
        const updatedChildImages = (originalImage.childImages || []).filter(child => child.id !== selectedImage.id);
        const updatedOriginalImage = {
          ...originalImage,
          childImages: updatedChildImages
        };
        setOriginalImage(updatedOriginalImage);
        setSelectedImage(updatedOriginalImage);
      }
      setShowDeleteConfirmation(false);
    } catch (error: any) {
      captureException("Failed to delete image", error);
      setShowDeleteConfirmation(false);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };
  const handleToggle = async (imageId: string) => {
    const currentVisibility = selectedImage.isPublic;
    const newVisibility = !currentVisibility;
    try {
      await onToggleVisibility(imageId, newVisibility);
      setSelectedImage(prev => ({
        ...prev,
        isPublic: newVisibility
      }));
      originalImage.childImages?.forEach(child => {
        if (child.id === imageId) {
          child.isPublic = newVisibility;
        }
      });
    } catch (error: any) {
      captureException("Failed to update visibility status.", error);
    }
  };
  useEffect(() => {
    if (selectedImage) {
      const fetchComments = async () => {
        const response = await getCommentsByImage(selectedImage.id, "top");
        if (response.comments) {
          setComments(response.comments);
        } else if (response.error) {
          console.error("Failed to fetch comments:", response.error);
        }
      };
      fetchComments();
    }
  }, [selectedImage]);
  const handleImageClick = (image: ImageDTO) => {
    setSelectedImage(image);
  };
  const handleViewClick = (imageId: string) => {
    router.push(`/images/${imageId}`);
  };
  const renderCarousel = () => {
    const images = [...(originalImage.parentImage ? [originalImage.parentImage] : []), originalImage, ...(originalImage.childImages || [])];
    return <Styled.CarouselContainer data-sentry-element="unknown" data-sentry-component="renderCarousel" data-sentry-source-file="index.tsx">
                {images.map(img => <Styled.CarouselItem key={img.id}>
                        {img.image.endsWith(".mp4") ? <Play className="w-8 h-8 text-white" style={{
          border: "2px solid white"
        }} /> : <Styled.ThumbnailImage src={img.image} alt={img.imageMetadata.description || "Image"} width={100} height={100} layout="fixed" $isSelected={img.id === selectedImage.id} onClick={() => handleImageClick(img)} />}

                        <Styled.ViewImageButton onClick={() => handleViewClick(img.id)}>
                            <VisibilityIcon />
                        </Styled.ViewImageButton>
                    </Styled.CarouselItem>)}
            </Styled.CarouselContainer>;
  };
  const formatRequestType = (requestType: string) => {
    if (!requestType) return "";
    return requestType.charAt(0).toUpperCase() + requestType.slice(1).toLowerCase();
  };
  useEffect(() => {
    if (selectedImage) {
      const fetchStats = async () => {
        let response;
        switch (selectedImage.imageMetadata.requestType) {
          case RequestType.MONSTER:
            response = await getMonsterStatsByImageId(selectedImage.id);
            break;
          case RequestType.NPC:
            response = await getNPCStatsByImageId(selectedImage.id);
            break;
          default:
            return;
        }
        if (!response) {
          setStats(null);
        } else {
          setStats(response);
        }
      };
      fetchStats();
    }
  }, [selectedImage]);
  const {
    theme,
    username,
    description,
    npcFieldsMap,
    requestType
  } = selectedImage.imageMetadata;
  const race = npcFieldsMap?.["Race"] || "";
  const characterClass = npcFieldsMap?.["Class"] || "";
  const occupation = npcFieldsMap?.["Occupation"] || "";
  const monster = npcFieldsMap?.["Monster"] || "";
  const itemName = npcFieldsMap?.["Item Name"] || "";
  const landscapeName = npcFieldsMap?.["Landscape Name"] || "";
  const battleMapName = npcFieldsMap?.["Battlemap Name"] || "";
  const itemType = npcFieldsMap?.["Item Type"] || "";
  const npcDescription = [race, characterClass, occupation, monster].filter(Boolean).join(" ") || description;
  const characterDescription = npcFieldsMap?.["description"] || "";
  const baseTitle = `CharGen: D&D ${formatRequestType(requestType)} Art by ${username}`;
  const baseDescription = `Explore AI-generated D&D ${requestType?.toLowerCase() || ""} art`;
  const MAX_TITLE_LENGTH = 60;
  const titleOptions = {
    NPC: `${baseTitle} - ${npcDescription}`,
    CHARACTER: `${baseTitle} - ${characterDescription} in ${theme}`,
    MONSTER: `${baseTitle} - ${npcDescription} in ${theme}`,
    ITEM: `${baseTitle} - ${itemName} - ${itemType}`,
    LANDSCAPE: `${baseTitle} - ${landscapeName}`,
    BATTLEMAP: `${baseTitle} - ${battleMapName}`,
    EDIT: `${baseTitle} - ${npcDescription}`
  };
  const getTitle = (requestType: string) => {
    const title = titleOptions[requestType as keyof typeof titleOptions];
    return title.length > MAX_TITLE_LENGTH ? baseTitle : title;
  };
  const descriptionOptions = {
    NPC: `${baseDescription} featuring a ${npcDescription} created by ${username}. Generate your own D&D NPC art with CharGen.`,
    CHARACTER: `${baseDescription} created by ${username}. ${description} Generate your own D&D character art with CharGen.`,
    MONSTER: `${baseDescription} featuring a ${npcDescription} created by ${username}. Generate your own D&D monster art with CharGen.`,
    ITEM: `${baseDescription} featuring ${itemName} - ${itemType} created by ${username}. Generate your own D&D item art with CharGen.`,
    LANDSCAPE: `${baseDescription} featuring ${landscapeName} created by ${username}. Generate your own D&D landscape art with CharGen.`,
    BATTLEMAP: `${baseDescription} featuring ${battleMapName} created by ${username}. Generate your own D&D battlemaps with CharGen.`,
    EDIT: `${baseDescription} featuring a ${npcDescription} created by ${username}. Generate your own D&D NPC art with CharGen.`
  };
  const metaTitle = getTitle(requestType);
  const metaDescription = descriptionOptions[requestType];
  const handleVote = async (imageId: string) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    const newVoteStatus = !selectedImage.isVotedByUser;
    try {
      await voteOnImage(imageId, newVoteStatus);
      setSelectedImage(prev => ({
        ...prev,
        isVotedByUser: newVoteStatus,
        voteCount: prev.voteCount + (newVoteStatus ? 1 : -1)
      }));
      originalImage.childImages?.forEach(child => {
        if (child.id === imageId) {
          child.isVotedByUser = newVoteStatus;
          child.voteCount += newVoteStatus ? 1 : -1;
        }
      });
    } catch (error: any) {
      captureException("Failed to update vote status.", error);
    }
  };
  const isUserImage = (imageUsername: string) => state.username === imageUsername;
  const renderMetadata = () => <Styled.MetaDataSection data-sentry-element="unknown" data-sentry-component="renderMetadata" data-sentry-source-file="index.tsx">
            <MetadataSection imageInfo={selectedImage} data-sentry-element="MetadataSection" data-sentry-source-file="index.tsx" />
            <SocialShare image={selectedImage} // Share the selected image
    description={metaDescription} data-sentry-element="SocialShare" data-sentry-source-file="index.tsx" />
        </Styled.MetaDataSection>;
  const renderComments = () => <CommentSection image={selectedImage} initialComments={comments} data-sentry-element="CommentSection" data-sentry-component="renderComments" data-sentry-source-file="index.tsx" />;
  const renderStats = useCallback(() => {
    return <StatsSection image={selectedImage} statsProps={stats} />;
  }, [selectedImage, stats]);
  const tabs = [{
    label: "Metadata",
    content: renderMetadata()
  }, {
    label: "Stats",
    content: renderStats()
  }, {
    label: `Comments (${calculateTotalCommentsSize(comments)})`,
    content: renderComments()
  }];
  useEffect(() => {
    const img = new Image();
    img.src = selectedImage.image;
    img.onload = () => {
      setImageDimensions({
        width: img.width,
        height: img.height
      });
    };
  }, [selectedImage.image]);
  const [showLabels, setShowLabels] = useState<boolean>(false);
  const handleResize = useCallback(() => {
    if (typeof window !== "undefined") {
      // Ensure window is defined
      if (window.innerWidth > 700) {
        setShowLabels(true);
      } else {
        setShowLabels(false);
      }
    }
  }, []);
  useEffect(() => {
    if (typeof window !== "undefined") {
      // Ensure window is defined
      window.addEventListener("resize", handleResize);
      // Initial check
      handleResize();
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [handleResize]);
  const isVideo = selectedImage.image.endsWith(".mp4");
  const aspectRatio = isVideo ? parseFloat(selectedImage.imageMetadata.npcFieldsMap["Size"].split(":")[0]) / parseFloat(selectedImage.imageMetadata.npcFieldsMap["Size"].split(":")[1]) : imageDimensions.width && imageDimensions.height ? imageDimensions.width / imageDimensions.height : 1;
  return <Styled.Main data-sentry-element="unknown" data-sentry-component="ImagePage" data-sentry-source-file="index.tsx">
            <Styled.Title data-sentry-element="unknown" data-sentry-source-file="index.tsx">{metaTitle}</Styled.Title>
            <Styled.StyledModal data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.ImageSection data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    {isVideo ? <Styled.ImageWrapper $aspectRatio={aspectRatio}>
                            <ReactPlayer url={selectedImage.image} style={{
            position: "absolute",
            top: 0,
            left: 0
          }} width="100%" height="100%" playing controls={true} loop muted playsinline />
                        </Styled.ImageWrapper> : <Styled.StyledImage src={selectedImage.webpImage || selectedImage.image} alt="Artwork" width={imageDimensions.width} height={imageDimensions.height} loading="eager" layout="responsive" style={{
          objectFit: "contain"
        }} />}
                    {selectedImage && <ControlsContainer imageInfo={selectedImage} handleToggle={handleToggle} handleShareClick={handleShareClick} handleCompetitionsClick={handleCompetitionsClick} handleEditClick={handleEditClick} handleADetailerClick={handleADetailerClick} handleFaceSwapClick={handleFaceSwapClick} handleBGRemovalClick={handleBGRemovalClick} handleInpaintClick={handleInpaintClick} handleUpscaleClick={handleUpscalerModalClick} prepareForDelete={prepareForDelete} handleVote={handleVote} isUserImage={isUserImage} showLabel={showLabels} />}
                    {renderCarousel()}
                </Styled.ImageSection>
                <Tabs tabs={tabs} data-sentry-element="Tabs" data-sentry-source-file="index.tsx" />
            </Styled.StyledModal>
            <ShareModal isOpen={isShareModalOpen} image={selectedImage} onClose={handleCloseShareModal} data-sentry-element="ShareModal" data-sentry-source-file="index.tsx" />
            <EligibleCompetitionsModal isOpen={showEligibleCompetitionsModal} onCancel={handleCloseEligibleCompetitionsModal} imageId={selectedImage.id} data-sentry-element="EligibleCompetitionsModal" data-sentry-source-file="index.tsx" />
            <DeleteConfirmationModal isOpen={showDeleteConfirmation} onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} text="image" data-sentry-element="DeleteConfirmationModal" data-sentry-source-file="index.tsx" />
            {editImageId && <ADetailerModal isOpen={isADetailerModalOpen} onClose={handleCloseADetailerModal} imageId={editImageId} isPremium={false} />}
            {editImageId && <EditModal isOpen={showEditModal} onCancel={handleEditCancel} imageId={editImageId} />}
            {editImageId && <FaceSwapModal isOpen={isFaceSwapModalOpen} onClose={handleCloseFaceSwapModal} imageId={editImageId} />}
            {editImageId && <BGRemovalModal isOpen={isBGRemovalModalOpen} onClose={handleCloseBGRemovalModal} imageId={editImageId} />}
            <CanvasModal isOpen={isCanvasModalOpen} onClose={handleCloseCanvasModal} imageDto={selectedImage} onMaskSubmit={handleMaskSubmit} data-sentry-element="CanvasModal" data-sentry-source-file="index.tsx" />
            <InpaintingModal isOpen={isInpaintingModalOpen} onCancel={handleCloseInpaintingModal} base64Image={base64Image!} originalImage={selectedImage.image} imageId={selectedImage.id} handleInpaintClick={handleInpaintClick} data-sentry-element="InpaintingModal" data-sentry-source-file="index.tsx" />
            <UpscalerModal isOpen={isUpscalerModalOpen} onClose={handleCloseUpscalerModal} imageId={selectedImage.id} data-sentry-element="UpscalerModal" data-sentry-source-file="index.tsx" />
        </Styled.Main>;
};